import { useEffect, useState } from 'react';
import { FormControl, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { identityApiRef, configApiRef } from '@backstage/core-plugin-api';

const DIVISION_MAP_API: Record<string, string> = {
  "AGU": "agu",
  "APD": "cul",
  "ATS": "ats",
  "BEST": "bes",
  "CATE": "ene",
  "CONC": "coc",
  "CONST": "con",
  "CORP": "cor",
  "DAT": "dtc",
  "ENER": "ene",
  "FORW": "fwd",
  "FUNDA": "fun",
  "INDUS": "con",
  "INFRA": "cai",
  "INMO": "liv",
  "INM": "inm",
  "STO": "inm",
  "LOCU": "loc",
  "LOGI": "ene",
  "MOB": "sil",
  "OAIN": "con",
  "SENE": "ene",
  "SERG": "cai",
  "SERV": "fac",
  "SERV_OLD": "ser",
  "SILE": "sil",
  "TRAN": "air"
}

const JENKINS_DIVISION_FOLDER: Record<string, string> = {
  agu: 'agua',
  ats: 'corporativo',
  bes: 'bestinver',
  coc: 'concesiones',
  con: 'construccion',
  cai: 'corporacion-acciona-infraestructura',
  cor: 'corporativo',
  dctm: 'documentum/job/documentum',
  ene: 'energia',
  inm: 'inmobiliaria',
  mob: 'mobility',
  ofc: 'oficina-devops',
  ser: 'servicios',
  sil: 'silence',
  fac: 'facility-services',
  air: 'airport-services',
  cul: 'cultura',
  loc: 'locus',
  fun: 'fundaciones',
  liv: 'living-culture',
  dtc: 'data-centers',
};

export const ApplicationProjectsIaCField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<any>) => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const [applications, setApplications] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [applicationRef, setApplicationRef] = useState('');
  const [projectRef, setProjectRef] = useState('');
  const [project, setProject] = useState('');

  // -----------------------------------------------------------------------------------
  // ✔️ 1. Sincronizar con formData
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    if (!formData) return;

    setApplicationRef(formData.applicationRef || '');
    setProjectRef(formData.projectRef || '');
    setProject(formData.project || '');
  }, [formData]);

  // -----------------------------------------------------------------------------------
  // ✔️ 2. Cargar TODAS las applications del catálogo
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    const fetchApplications = async () => {
      const resp = await catalogApi.getEntities({
        filter: { kind: 'Application' },
      });
      setApplications(resp.items);
    };

    fetchApplications();
  }, [catalogApi]);

  // -----------------------------------------------------------------------------------
  // ✔️ 3. Cargar proyectos donde ops-team es operator Y el usuario tiene permisos
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    const fetchAuthorizedProjects = async () => {
      const backendUrl = config.getString('backend.baseUrl');
      const token = (await identityApi.getCredentials()).token;
      
      // Get current user (or impersonated)
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef;
      
      // Obtener email del usuario desde el catálogo
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        if (profile?.email) {
          userEmail = profile.email;
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch user entity, using username instead:', err);
      }
      
      // Check for impersonated user
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
      
      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) {
            effectiveUser = impersonatedProfile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
        }
        console.log('🎭 Impersonating user:', effectiveUser, '(from ApplicationProjectsIaCField)');
      }
      
      // 1. Query OpenFGA for projects marked with ops_marker operator relation
      const opsTeamResponse = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user: "platform:main",
          type: "project",
          relation: "ops_marker",
        }),
      });
      
      const opsTeamData = await opsTeamResponse.json();
      const opsTeamProjects = (opsTeamData.objects ?? [])
        .map((p: string) => {
          const match = p.match(/^project:(prj\d{7})$/i);
          return match ? match[1].toLowerCase() : null;
        })
        .filter(Boolean) as string[];
      
      console.log('🔧 Ops-team operator projects:', opsTeamProjects);
      
      // 2. Query OpenFGA for projects where user has permissions
      const userResponse = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user: "user:" + effectiveUser,
          type: "project",
        }),
      });
      
      const userData = await userResponse.json();
      const userProjects = (userData.objects ?? [])
        .map((p: string) => {
          const match = p.match(/^project:(prj\d{7})$/i);
          return match ? match[1].toLowerCase() : null;
        })
        .filter(Boolean) as string[];

      console.log('👤 User authorized projects:', userProjects);


      // 3. Intersección: proyectos donde ops-team es operator Y el usuario tiene permisos
      const authorizedProjectCodes = opsTeamProjects.filter(code => 
        userProjects.includes(code)
      );

      console.log('✅ Projects with both ops-team operator AND user permissions:', authorizedProjectCodes);
      if (authorizedProjectCodes.length === 0) {
        setProjects([]);
        setIsAuthorized(false);
        setAuthError('No projects found where both ops-team has operator access and you have permissions.');
        return;
      }
      
      // 4. Fetch project entities from catalog
      const resp = await catalogApi.getEntities({
        filter: { kind: 'Project' },
      });
      
      const filteredProjects = resp.items.filter(p =>
        authorizedProjectCodes.includes(String(p.spec?.number ?? '').toLowerCase())
      );
      
      console.log('📦 Loaded authorized projects from catalog:', filteredProjects.length);
      setProjects(filteredProjects);
      setIsAuthorized(filteredProjects.length > 0);
      
      if (filteredProjects.length === 0) {
        setAuthError('No matching projects found in catalog.');
      } else {
        setAuthError(null);
      }
    };

    fetchAuthorizedProjects();
  }, [catalogApi, identityApi, config]);


  // -----------------------------------------------------------------------------------
  // ✔️ 4. Handle de selección de aplicación
  // -----------------------------------------------------------------------------------
  const handleApplicationChange = async (app: any) => {
    const ref = `application:${app.metadata.namespace}/${app.metadata.name}`;

    const number = app.spec.number || '';
    const owner = app.spec.owner || '';

    const company: any = await catalogApi.getEntityByRef(app.spec.division);
    const divisionRef = company.spec.subDivisionOf;
    const division: any = await catalogApi.getEntityByRef(divisionRef);
    const jenkinsApplicationAPM = `${DIVISION_MAP_API[division.spec.company_id]}_${number}`;
    const jenkinsDivisionFolder = JENKINS_DIVISION_FOLDER[DIVISION_MAP_API[division.spec.company_id]];

    setApplicationRef(ref);

    onChange({
      application: number,
      applicationName: app.metadata.name,
      applicationRef: ref,
      applicationOwnerRef: owner,
      applicationDivision: division.spec.company_id,
      jenkinsApplicationAPM: jenkinsApplicationAPM,
      jenkinsDivisionFolder: jenkinsDivisionFolder,
      project,
      projectRef,
      isAuthorized,
    });
  };

  // -----------------------------------------------------------------------------------
  // ✔️ 5. Handle de selección de proyecto
  // -----------------------------------------------------------------------------------
  const handleProjectChange = (proj: any) => {
    const ref = `project:${proj.metadata.namespace}/${proj.metadata.name}`;
    const number = proj.spec.number || '';

    setProject(number);
    setProjectRef(ref);

    onChange({
      ...(formData ?? {}),
      project: number,
      projectRef: ref,
      isAuthorized,
    });
  };

  // -----------------------------------------------------------------------------------
  // ✔️ Render
  // -----------------------------------------------------------------------------------
  return (
    <FormControl margin="normal" required={required} fullWidth>
      {/* Application */}
      <Autocomplete
        options={applications}
        disabled={!isAuthorized}
        getOptionLabel={opt => opt.metadata.title || ''}
        value={
          applications.find(
            a => `application:${a.metadata.namespace}/${a.metadata.name}` === applicationRef,
          ) || null
        }
        onChange={(_, newValue) => {
          if (newValue && isAuthorized) handleApplicationChange(newValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Application"
            required
            error={!!authError}
            helperText={authError || rawErrors?.[0] || 'Search for an Application'}
          />
        )}
      />

      {/* Project (from ops-team operator relation) */}
      <Autocomplete
        options={projects}
        disabled={!isAuthorized}
        getOptionLabel={opt => opt.metadata.title || ''}
        value={
          projects.find(
            p => `project:${p.metadata.namespace}/${p.metadata.name}` === projectRef,
          ) || null
        }
        onChange={(_, newValue) => {
          if (newValue) handleProjectChange(newValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Project"
            required
            helperText={
              !isAuthorized
                ? authError || 'No projects available'
                : rawErrors?.[0] || 'Select a project where ops-team has operator access and you have permissions'
            }
          />
        )}
      />
    </FormControl>
  );
};

// -----------------------------------------------------------------------------------
// ✔️ Validación
// -----------------------------------------------------------------------------------
export const applicationProjectsIaCCaseValidation = (
  value: { application?: string; project?: string; isAuthorized?: boolean },
  validation: FieldValidation,
) => {
  if (!value?.application) {
    validation.addError('Application is required.');
  }

  if (!value?.project) {
    validation.addError('Project is required.');
  }

  if (value?.isAuthorized === false) {
    validation.addError(
      'You are not authorized to use this template. You need access to projects where ops-team has operator permissions.',
    );
  }
};


// -----------------------------------------------------------------------------------
// ✔️ Registro del Custom Field
// -----------------------------------------------------------------------------------
export const ApplicationProjectsIaCCaseFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApplicationProjectsIaCField',
    component: ApplicationProjectsIaCField,
    validation: applicationProjectsIaCCaseValidation,
  }),
);
